import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RequestStatus,
  RequestStatusArray,
  DiningRequest,
  PaginatedResponse,
} from "../types/request";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { createDiningRequestsApi, type DiningRequestParams } from "../api/diningRequestsApi";
import { useEffect, useState, useMemo } from "react";

interface UseDiningRequestsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  status?: RequestStatus | RequestStatusArray;
  paymentStatus?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const useDiningRequests = ({
  hotelId,
  initialPage = 1,
  initialLimit = 10,
  status,
  paymentStatus,
  sortBy,
  sortOrder = "desc",
}: UseDiningRequestsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();
  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Create API instance
  const api = useMemo(() => createDiningRequestsApi(callApi), [callApi]);

  // Fetch all dining requests with pagination
  const { data, isLoading, error, refetch } = useQuery<
    PaginatedResponse<DiningRequest>,
    Error
  >({
    queryKey: [
      "diningRequests",
      hotelId,
      pagination.page,
      pagination.limit,
      // Normalize status for queryKey: if array, sort & join
      Array.isArray(status) ? [...status].sort().join(",") : status,
      paymentStatus,
      searchTerm,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const params: DiningRequestParams & { paymentStatus?: string } = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (status) {
        if (Array.isArray(status)) {
          params.status = status.join(",");
        } else {
          params.status = status;
        }
      }

      if (paymentStatus) {
        params.paymentStatus = paymentStatus;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      return await api.fetchDiningRequests(params);
    },
    refetchInterval: 30000,
  });

  // Update total count when data changes
  useEffect(() => {
    if (data?.meta?.totalCount !== undefined) {
      if (data.meta.totalCount !== pagination.total) {
        setTotal(data.meta.totalCount);
      }
    }
  }, [data?.meta?.totalCount, setTotal, pagination.total]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [status, paymentStatus, setPage]);

  // Update request status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      requestId,
      status,
      completedAt,
    }: {
      requestId: string;
      status: RequestStatus;
      completedAt?: string;
    }) => {
      return await api.updateDiningRequestStatus(requestId, {
        status,
        completedAt,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diningRequests"] });
    },
  });

  // Update payment status mutation
  const updatePaymentMutation = useMutation({
    mutationFn: async ({
      requestId,
      paymentStatus,
      paymentMethod,
      notes,
    }: {
      requestId: string;
      paymentStatus: string;
      paymentMethod?: string;
      notes?: string;
    }) => {
      await callApi({
        method: "PUT",
        url: `/management/dining/${requestId}/paymentEndpoint`,
        data: {
          paymentStatus,
          paymentMethod,
          notes,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["diningRequests"] });
    },
  });

  const fetchRequestById = async (requestId: string) => {
    return await api.fetchDiningRequestById(requestId);
  };

  return {
    requests: data?.data || [],
    isLoading,
    error,
    updateRequestStatus: updateStatusMutation.mutate,
    updateRequestStatusAsync: updateStatusMutation.mutateAsync,
    updatePaymentStatus: updatePaymentMutation.mutate,
    fetchRequestById,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  };
};
